import { useId, CSSProperties } from 'react'

interface BuretteProps {
  volume?: number // current volume in mL
  maxVolume?: number // max capacity (typically 50mL)
  liquidColor?: string
  stopcock?: 'open' | 'closed'
  onStopcock?: () => void
  onDrip?: () => void
  disabled?: boolean
  className?: string
}

const Burette = ({
  volume = 25,
  maxVolume = 50,
  liquidColor = '#7DD3FC',
  stopcock = 'closed',
  onStopcock,
  onDrip,
  disabled = false,
  className = ''
}: BuretteProps) => {
  const id = useId()

  // Calculate fill height (inverted - 0 at top, max at bottom for burette)
  const fillPercentage = volume / maxVolume
  const tubeTop = 15
  const tubeBottom = 145
  const tubeHeight = tubeBottom - tubeTop
  const fillHeight = fillPercentage * tubeHeight
  const fillY = tubeTop + 5 // Liquid starts from top

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  const stopcockStyle: CSSProperties = {
    cursor: disabled ? 'default' : onStopcock ? 'pointer' : 'default',
    transition: 'transform 0.3s ease'
  }

  return (
    <svg
      viewBox="0 0 70 200"
      width={90}
      height={260}
      style={containerStyle}
      className={className}
    >
      <defs>
        {/* Glass gradient */}
        <linearGradient id={`glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(200,220,240,0.7)" />
          <stop offset="15%" stopColor="rgba(255,255,255,0.4)" />
          <stop offset="50%" stopColor="rgba(220,235,250,0.25)" />
          <stop offset="85%" stopColor="rgba(255,255,255,0.35)" />
          <stop offset="100%" stopColor="rgba(200,220,240,0.6)" />
        </linearGradient>

        {/* Liquid gradient */}
        <linearGradient id={`liquid-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={liquidColor} stopOpacity="0.85" />
          <stop offset="100%" stopColor={liquidColor} stopOpacity="0.95" />
        </linearGradient>

        {/* Stopcock plastic gradient */}
        <linearGradient id={`stopcock-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#2C2C2C" />
          <stop offset="50%" stopColor="#4A4A4A" />
          <stop offset="100%" stopColor="#2C2C2C" />
        </linearGradient>

        {/* Shadow */}
        <filter id={`shadow-${id}`} x="-20%" y="-5%" width="140%" height="110%">
          <feDropShadow dx="2" dy="2" stdDeviation="2" floodOpacity="0.25" />
        </filter>

        {/* Clip for liquid */}
        <clipPath id={`liquid-clip-${id}`}>
          <rect x="28" y={tubeTop} width="14" height={tubeHeight} />
        </clipPath>
      </defs>

      <g filter={`url(#shadow-${id})`}>
        {/* Top funnel / reservoir */}
        <path
          d="M25 8 L25 15 L28 15 L28 8 L42 8 L42 15 L45 15 L45 8 Q45 3 35 3 Q25 3 25 8"
          fill={`url(#glass-${id})`}
          stroke="#7A9BB8"
          strokeWidth="1.5"
        />

        {/* Top rim */}
        <ellipse
          cx="35"
          cy="5"
          rx="12"
          ry="3"
          fill="rgba(200,220,240,0.5)"
          stroke="#7A9BB8"
          strokeWidth="1"
        />

        {/* Main tube body */}
        <rect
          x="28"
          y="15"
          width="14"
          height="130"
          fill={`url(#glass-${id})`}
          stroke="#7A9BB8"
          strokeWidth="1.5"
          rx="1"
        />

        {/* Liquid inside */}
        {volume > 0 && (
          <g clipPath={`url(#liquid-clip-${id})`}>
            <rect
              x="29"
              y={fillY}
              width="12"
              height={fillHeight}
              fill={`url(#liquid-${id})`}
            />
            {/* Meniscus */}
            <ellipse
              cx="35"
              cy={fillY + fillHeight}
              rx="5"
              ry="2"
              fill={liquidColor}
              opacity="0.8"
            />
          </g>
        )}

        {/* Glass shine */}
        <line
          x1="30"
          y1="20"
          x2="30"
          y2="140"
          stroke="rgba(255,255,255,0.5)"
          strokeWidth="1.5"
          strokeLinecap="round"
        />

        {/* Graduation marks */}
        {Array.from({ length: 11 }, (_, i) => {
          const markVolume = i * 5
          const y = tubeTop + 5 + (markVolume / maxVolume) * (tubeHeight - 10)
          const isMajor = markVolume % 10 === 0
          return (
            <g key={i}>
              <line
                x1={42}
                y1={y}
                x2={isMajor ? 48 : 45}
                y2={y}
                stroke="#5A7A95"
                strokeWidth={isMajor ? 1.2 : 0.8}
              />
              {isMajor && (
                <text
                  x="52"
                  y={y + 3}
                  fontSize="7"
                  fill="#5A7A95"
                  fontFamily="Arial, sans-serif"
                >
                  {markVolume}
                </text>
              )}
            </g>
          )
        })}

        {/* mL label */}
        <text
          x="52"
          y="22"
          fontSize="6"
          fill="#5A7A95"
          fontFamily="Arial, sans-serif"
        >
          mL
        </text>

        {/* Stopcock assembly */}
        <g
          style={stopcockStyle}
          onClick={!disabled && onStopcock ? onStopcock : undefined}
        >
          {/* Stopcock body */}
          <rect
            x="30"
            y="148"
            width="10"
            height="12"
            fill={`url(#stopcock-${id})`}
            rx="2"
          />

          {/* Stopcock handle */}
          <rect
            x={stopcock === 'open' ? 40 : 32}
            y="151"
            width={stopcock === 'open' ? 18 : 6}
            height="6"
            fill="#FF5A36"
            rx="2"
            style={{ transition: 'all 0.3s ease' }}
          />

          {/* Handle grip lines */}
          {stopcock === 'open' && (
            <>
              <line x1="45" y1="152" x2="45" y2="156" stroke="#CC4A2A" strokeWidth="1" />
              <line x1="50" y1="152" x2="50" y2="156" stroke="#CC4A2A" strokeWidth="1" />
              <line x1="55" y1="152" x2="55" y2="156" stroke="#CC4A2A" strokeWidth="1" />
            </>
          )}
        </g>

        {/* Tip below stopcock */}
        <path
          d="M33 160 L33 175 Q33 180 35 180 Q37 180 37 175 L37 160"
          fill={`url(#glass-${id})`}
          stroke="#7A9BB8"
          strokeWidth="1"
        />

        {/* Tip opening */}
        <ellipse
          cx="35"
          cy="180"
          rx="1.5"
          ry="0.8"
          fill="#5A7A95"
        />

        {/* Dripping animation when stopcock open */}
        {stopcock === 'open' && volume > 0 && (
          <ellipse
            cx="35"
            cy="185"
            rx="2"
            ry="3"
            fill={liquidColor}
            opacity="0.8"
          >
            <animate
              attributeName="cy"
              values="182;195;182"
              dur="0.6s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0.8;0;0.8"
              dur="0.6s"
              repeatCount="indefinite"
            />
          </ellipse>
        )}
      </g>

      {/* Label */}
      <text
        x="35"
        y="198"
        fontSize="9"
        fill="#E0E0E0"
        textAnchor="middle"
        fontWeight="500"
      >
        Burette
      </text>
    </svg>
  )
}

export default Burette
